import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { TestingHqContext } from '../../context/TestingHqContext';
import { UserContext } from '../../context/UserContext';

import RoundSelector from '../../components/RoundSelector';
import { get_testing_place_list, get_testing_accept_summary } from '../../utils/restapi';

import './branch.css';

/**
 * 회차별 고사장현황
 *  선택한 회차·일정에 대해 내 지역본부가 운영하는 고사장 목록과
 *  고사실 수 / 접수인원을 보여준다.
 */
const BranchPlaceStat = () => {
  const { testingHqs } = useContext(TestingHqContext);
  const { user } = useContext(UserContext);

  const [selection, setSelection] = useState({ round: null, scheduleId: '' });
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const myHqCodes = useMemo(() => testingHqs.map((hq) => hq.code).filter(Boolean), [testingHqs]);
  const myHqCodesKey = myHqCodes.join(',');

  const handleSelectionChange = useCallback((next) => setSelection(next), []);
  const handleSelectorError = useCallback((message) => setErrorMessage(message), []);

  const testingId = selection.round?.testing_id;
  const scheduleId = selection.scheduleId;

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      if (!testingId) {
        setRows([]);
        return;
      }
      if (!myHqCodes.length) {
        setRows([]);
        setErrorMessage('소속 지역본부 정보가 없습니다.');
        return;
      }

      setIsLoading(true);
      setErrorMessage('');

      try {
        // 고사장 목록은 내 지역본부 소속만(gcodes), 고사실수·접수인원은 집계 API 로 채운다.
        const summaryParams = { testing_id: Number(testingId) };
        if (scheduleId) summaryParams.testing_schedule_id = Number(scheduleId);

        const [placeResponse, summaryResponse] = await Promise.all([
          get_testing_place_list({
            person_id: user?.person_id || 0,
            testing_id: Number(testingId),
            gcodes: myHqCodes.join(','),
            page: 1,
            rowsPerPage: 10000,
            sort: 'a_gcode,a_code',
            isAll: false,
          }),
          get_testing_accept_summary(summaryParams),
        ]);

        if (cancelled) return;

        const places = placeResponse?.lists || [];
        const summaryMap = new Map();
        (summaryResponse?.lists || []).forEach((row) => {
          summaryMap.set(Number(row.testing_place_id), row);
        });

        const nextRows = places
          .map((place) => {
            const summary = summaryMap.get(Number(place.testing_place_id));
            return {
              testing_place_id: place.testing_place_id,
              name: place.name || '-',
              rooms: Number(summary?.rooms) || 0,
              acceptUsers: Number(summary?.accept_users) || 0,
            };
          })
          .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ko'));

        setRows(nextRows);
      } catch (error) {
        console.error('Error fetching place stat:', error);
        if (!cancelled) {
          setRows([]);
          setErrorMessage('고사장 현황을 불러오는 중 오류가 발생했습니다.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadData();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testingId, scheduleId, myHqCodesKey]);

  const summary = useMemo(() => rows.reduce(
    (acc, row) => {
      acc.places += 1;
      acc.rooms += row.rooms;
      acc.acceptUsers += row.acceptUsers;
      return acc;
    },
    { places: 0, rooms: 0, acceptUsers: 0 }
  ), [rows]);

  return (
    <div id="branch_page">
      <div className="head">
        <h1>회차별 고사장현황</h1>
      </div>
      <div className="body">
        <div className="top">
          <ul className="form">
            <RoundSelector onChange={handleSelectionChange} onError={handleSelectorError} />
          </ul>
        </div>

        <div className="stat_summary">
          <div className="summary_card">
            <strong>고사장</strong>
            <span>{summary.places}</span>
          </div>
          <div className="summary_card">
            <strong>고사실</strong>
            <span>{summary.rooms}</span>
          </div>
          <div className="summary_card">
            <strong>접수인원</strong>
            <span>{summary.acceptUsers}</span>
          </div>
        </div>

        <div className="middle stat_table_wrap">
          {errorMessage ? <p className="stat_error">{errorMessage}</p> : null}

          <table className="stat_table">
            <thead>
              <tr>
                <th>NO.</th>
                <th>고사장</th>
                <th>고사실수</th>
                <th>접수인원</th>
              </tr>
            </thead>
            <tbody>
              {!rows.length ? (
                <tr>
                  <td colSpan={4} className="stat_empty">
                    {isLoading ? '데이터를 불러오는 중입니다.' : '조회된 데이터가 없습니다.'}
                  </td>
                </tr>
              ) : null}

              {rows.map((row, index) => (
                <tr key={row.testing_place_id}>
                  <td>{index + 1}</td>
                  <td>{row.name}</td>
                  <td>{row.rooms}</td>
                  <td>{row.acceptUsers}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="stat_total_row">
                <td colSpan={2}>합계</td>
                <td>{summary.rooms}</td>
                <td>{summary.acceptUsers}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BranchPlaceStat;
